import { useState } from "react";
import { StoreEmployee } from "@/models/storeModel";
import { useStoreManagementStore } from "@/store/storeManagement";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";
import { StaffTable } from "./StaffTable";
import { StaffTableSkeleton } from "./StaffTableSkeleton";
import { ConfirmRoleChangeDialog } from "./ConfirmRoleChangeDialog";
import { useTranslation } from "react-i18next";

interface StaffListProps {
  staff: StoreEmployee[];
  isLoading: boolean;
  isAdminHQ: boolean;
}

export function StaffList({ staff, isLoading, isAdminHQ }: StaffListProps) {
  const { updateEmployeeRole, removeEmployee } = useStoreManagementStore();
  const [employeeToDelete, setEmployeeToDelete] =
    useState<StoreEmployee | null>(null);
  const [roleChangeEmployee, setRoleChangeEmployee] = useState<{
    employee: StoreEmployee;
    newRole: string;
  } | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleRoleChange = async (
    employee: StoreEmployee,
    role: string
  ): Promise<void> => {
    try {
      await updateEmployeeRole(
        employee.id,
        role as "admin" | "manager" | "staff" | "super_admin"
      );
      toast({
        title: t("roleUpdated"),
        description: t("roleUpdatedDescription"),
      });
      setRoleChangeEmployee(null);
      return Promise.resolve();
    } catch (error) {
      toast({
        title: t("error"),
        description: t("failedToUpdateRole"),
        variant: "destructive",
      });
      return Promise.reject(error);
    }
  };

  // Modified function to return a Promise
  const confirmRoleChange = (
    employee: StoreEmployee,
    newRole: string
  ): Promise<void> => {
    setRoleChangeEmployee({
      employee,
      newRole,
    });
    // Return a resolved promise to satisfy the type requirement
    return Promise.resolve();
  };

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;

    try {
      await removeEmployee(employeeToDelete.id);
      toast({
        title: "Staff removed",
        description: "The staff member has been removed successfully.",
      });
      setEmployeeToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove the staff member.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <StaffTableSkeleton />;
  }

  return (
    <>
      <StaffTable
        staff={staff}
        isAdminHQ={isAdminHQ}
        onDeleteEmployee={(employee) => setEmployeeToDelete(employee)}
        onRoleChange={confirmRoleChange}
      />

      {employeeToDelete && (
        <ConfirmDeleteDialog
          open={!!employeeToDelete}
          onOpenChange={(isOpen) => !isOpen && setEmployeeToDelete(null)}
          onConfirm={handleDeleteEmployee}
          title="Remove Staff Member"
          description={`Are you sure you want to remove ${employeeToDelete.firstName} ${employeeToDelete.lastName}? This action cannot be undone.`}
        />
      )}

      {roleChangeEmployee && (
        <ConfirmRoleChangeDialog
          employee={roleChangeEmployee.employee}
          newRole={roleChangeEmployee.newRole}
          open={!!roleChangeEmployee}
          onOpenChange={(open) => !open && setRoleChangeEmployee(null)}
          onConfirm={() =>
            handleRoleChange(
              roleChangeEmployee.employee,
              roleChangeEmployee.newRole
            )
          }
        />
      )}
    </>
  );
}
